import { ToastService } from '@app/ui/shared/toast/toast.service';
import { inject, Injectable } from '@angular/core';
import { NgStorage } from '@core/enums/ngStorage/ngStorage.enum';
import { User, UserPermission } from '@core/interface/users/loginResponse';
import { INavData } from '@coreui/angular';
import { UserType } from '../../enums/user/userType.enum';
import { NavItem } from '../../interface/nav_items/nav_items.interface';
import { environment } from '@core/environments/environments';
import { RolePermissionItem } from '../../interface/roles/roles.interface';
import { NAV_ITEMS_CLIENT } from '../../nav_Items/client/nav_client.nav';
import { NAV_ITEMS_OFFICIAL } from '../../nav_Items/offical/nav_official.nav';
import { NAV_ITEMS_PROVIDER } from '../../nav_Items/provider/nav_provider.nav';
import { NAV_ITEMS_SUPERADMIN } from '../../nav_Items/superadmin/nav_superadmin.nav';
import { StorageService } from '../ngStorage/storage.service';
import { UserSessionService } from '../user-session/user-session.service';

// nav.service.ts
@Injectable({ providedIn: 'root' })
export class NavService {
  private _userSessionService = inject(UserSessionService);
  private _toastService = inject(ToastService);
  private _storageService = inject(StorageService);

  async getNavItems(): Promise<INavData[]> {
    let user = this._userSessionService.currentUser as User | null;
    if (!user) {
      user = (await this._storageService.getStorage(NgStorage.USER)) as User | null;
    }
    if (!user?.userType) {
      this._toastService.danger('No se pudo obtener el tipo de usuario');
      return [];
    }

    const baseItems = this.getNavByUserType(user.userType);
    const isTravelfest = user.agencyId === environment.travelfestAgencyId;
    const agencyFiltered = this.filterByAgency(baseItems, isTravelfest);

    if (user.userType === UserType.SUPERADMIN) {
      return agencyFiltered;
    }

    const perms = (await this._storageService.getStorage(
      NgStorage.USER_PERMISSIONS,
    )) as UserPermission[];
    if (!perms?.length) {
      return agencyFiltered;
    }
    const rawBasePermissions = (await this._storageService.getStorage(
      NgStorage.BASE_PERMISSIONS,
    )) as RolePermissionItem[] | Record<string, RolePermissionItem[]> | null;
    const basePermissions = this.normalizeBasePermissions(rawBasePermissions);

    const resolvedItems = this.resolvePermissionIds(agencyFiltered, basePermissions);
    return this.filterByPermissions(resolvedItems, perms);
  }

  private getNavByUserType(userType: UserType): NavItem[] {
    let items: NavItem[];
    switch (userType) {
      case UserType.OFFICIAL:
        items = NAV_ITEMS_OFFICIAL;
        break;
      case UserType.CLIENT:
        items = NAV_ITEMS_CLIENT;
        break;
      case UserType.PROVIDER:
        items = NAV_ITEMS_PROVIDER;
        break;
      case UserType.SUPERADMIN:
        items = NAV_ITEMS_SUPERADMIN;
        break;
      default:
        return [];
    }
    return items;
  }

  private filterByAgency(items: NavItem[], isTravelfest: boolean): NavItem[] {
    if (!isTravelfest) return items;
    return items
      .map((item) => {
        if (item.childAgencyOnly) return null;
        if (item.children?.length) {
          const children = item.children.filter((c) => !(c as NavItem).childAgencyOnly);
          return { ...item, children };
        }
        return item;
      })
      .filter((item): item is NavItem => !!item);
  }

  private filterByPermissions(items: NavItem[], perms: UserPermission[]): NavItem[] {
    const allowedIds = this.getAllowedPermissionIds(perms);
    const allowedLabels = this.getAllowedPermissionLabels(perms);
    items = items
      .map((item) => {
        if (item.children?.length) {
          const children = this.filterByPermissions(item.children as NavItem[], perms);
          if (!children.length) {
            return null;
          }
          return { ...item, children };
        }
        return this.hasPermission(item, allowedIds, allowedLabels) ? item : null;
      })
      .filter((item): item is NavItem => !!item);

    return items;
  }

  async hasPermissions(permissions: UserPermission | UserPermission[]): Promise<boolean> {
    const perms = (await this._storageService.getStorage(NgStorage.USER_PERMISSIONS)) as
      | UserPermission[]
      | null;
    if (!perms?.length) {
      return false;
    }
    const requiredPermissions = Array.isArray(permissions) ? permissions : [permissions];
    if (!requiredPermissions.length) {
      return false;
    }
    return this.hasAnyPermission(
      requiredPermissions,
      this.getAllowedPermissionIds(perms),
      this.getAllowedPermissionLabels(perms),
    );
  }

  async hasPermissionLabels(labels: string | string[]): Promise<boolean> {
    const perms = (await this._storageService.getStorage(NgStorage.USER_PERMISSIONS)) as
      | UserPermission[]
      | null;
    if (!perms?.length) {
      return false;
    }
    const requiredLabels = Array.isArray(labels) ? labels : [labels];
    if (!requiredLabels.length) {
      return false;
    }
    const allowedLabels = this.getAllowedPermissionLabels(perms);
    return requiredLabels.some((label) => {
      const normalized = this.normalizePermissionLabel(label);
      return !!normalized && allowedLabels.has(normalized);
    });
  }

  async hasPermissionGroups(groups: string | string[]): Promise<boolean> {
    const perms = (await this._storageService.getStorage(NgStorage.USER_PERMISSIONS)) as
      | UserPermission[]
      | null;
    if (!perms?.length) {
      return false;
    }
    const normalizedGroups = this.normalizePermissionGroups(groups);
    if (!normalizedGroups.length) {
      return false;
    }
    const allowedLabels = this.getAllowedPermissionLabels(perms);
    return normalizedGroups.some((group) => {
      for (const label of allowedLabels) {
        if (label.startsWith(group)) {
          return true;
        }
      }
      return false;
    });
  }

  private hasPermission(
    item: NavItem,
    allowedIds: Set<number>,
    allowedLabels: Set<string>,
  ): boolean {
    if (!item.permissionIds?.length) {
      if (item.permissionGroup || item.permissionCategory) {
        return false;
      }
      return true;
    }
    return this.hasAnyPermission(item.permissionIds, allowedIds, allowedLabels);
  }

  private hasAnyPermission(
    permissions: UserPermission[],
    allowedIds: Set<number>,
    allowedLabels: Set<string>,
  ): boolean {
    return permissions.some((permission) => {
      if (permission.permisoId && allowedIds.has(permission.permisoId)) {
        return true;
      }
      const label = this.normalizePermissionLabel(permission.permiso);
      return !!label && allowedLabels.has(label);
    });
  }

  private getAllowedPermissionIds(perms: UserPermission[]): Set<number> {
    return new Set(perms.filter((perm) => perm.allowed).map((perm) => perm.permisoId));
  }

  private getAllowedPermissionLabels(perms: UserPermission[]): Set<string> {
    return new Set(
      perms
        .filter((perm) => perm.allowed)
        .map((perm) => this.normalizePermissionLabel(perm.permiso))
        .filter((label): label is string => !!label),
    );
  }

  private normalizePermissionLabel(label?: string): string | null {
    return label
      ? label
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
      : null;
  }

  private normalizePermissionGroups(groups?: string | string[]): string[] {
    if (!groups) {
      return [];
    }
    const list = Array.isArray(groups) ? groups : [groups];
    return list
      .map((group) => this.normalizePermissionLabel(group))
      .filter((group): group is string => !!group);
  }

  private resolvePermissionIds(items: NavItem[], basePermissions: RolePermissionItem[]): NavItem[] {
    if (!basePermissions.length) {
      return items;
    }
    return items.map((item) => {
      const children = item.children
        ? (this.resolvePermissionIds(item.children as NavItem[], basePermissions) as NavItem[])
        : undefined;
      let permissionIds = item.permissionIds;
      if (!permissionIds?.length && (item.permissionGroup || item.permissionCategory)) {
        const selected = this.selectBasePermissionsForItem(item, basePermissions);
        if (selected.length) {
          permissionIds = selected.map((permission) => ({
            permisoId: permission.id,
            allowed: true,
            permiso: permission.label,
          }));
        }
      }
      return {
        ...item,
        ...(permissionIds ? { permissionIds } : {}),
        ...(children ? { children } : {}),
      };
    });
  }

  private selectBasePermissionsForItem(
    item: NavItem,
    basePermissions: RolePermissionItem[],
  ): RolePermissionItem[] {
    const category = this.normalizePermissionLabel(item.permissionCategory);
    const groups = this.normalizePermissionGroups(item.permissionGroup);
    if (!category && !groups.length) {
      return [];
    }
    return basePermissions.filter((permission) => {
      const permissionCategory = this.normalizePermissionLabel(permission.categoria_padre);
      if (category && permissionCategory !== category) {
        return false;
      }
      if (!groups.length) {
        return true;
      }
      const label = this.normalizePermissionLabel(permission.label);
      return !!label && groups.some((group) => label.startsWith(group));
    });
  }

  private normalizeBasePermissions(
    basePermissions: RolePermissionItem[] | Record<string, RolePermissionItem[]> | null,
  ): RolePermissionItem[] {
    if (!basePermissions) {
      return [];
    }
    if (Array.isArray(basePermissions)) {
      return basePermissions;
    }
    return Object.values(basePermissions)
      .filter((value): value is RolePermissionItem[] => Array.isArray(value))
      .flat();
  }
}
